// ============================================
// Wizard Steps Rendering
// 7단계 UI 렌더링
// ============================================

import { escapeHtml } from '../../utils.js';

// ============================================
// 점술 스타일 태그 (15개) - 점술가 성격/스타일
// ============================================

const FORTUNE_STYLE_TAGS = [
  '신비로운', '친근한', '직설적인', '따뜻한', '엄격한',
  '유머러스', '진지한', '부드러운', '카리스마', '차분한',
  '열정적인', '지혜로운', '다정한', '냉철한', '낙천적인'
];

// ============================================
// 점술 유형 태그 (14개) - 주요 점술 종류
// ============================================

const FORTUNE_TYPE_TAGS = [
  '사주명리', '타로', '꿈해몽', '손금', '관상',
  '별자리', '주역', '풍수', '작명', '궁합',
  '신점', '꽃점', '오라/기', '전생'
];

// ============================================
// 전문 분야 태그 (15개) - 점술 전문 분야
// ============================================

const SPECIALTY_TAGS = [
  '연애운', '재물운', '건강운', '직장운', '시험운',
  '이직운', '결혼운', '가족운', '대인관계', '사업운',
  '이사/이동', '해외운', '자녀운', '2025년운세', '올해운세'
];

// ============================================
// 메인 렌더 함수
// ============================================

export function renderStep(step, state) {
  switch (step) {
    case 1:
      return renderStep1(state);
    case 2:
      return renderStep2(state);
    case 3:
      return renderStep3(state);
    case 4:
      return renderStep4(state);
    case 5:
      return renderStep5(state);
    case 6:
      return renderStep6(state);
    case 7:
      return renderStep7(state);
    default:
      return '';
  }
}

// ============================================
// Step 1: 성별 선택
// ============================================

function renderStep1(state) {
  return `
    <div class="wizard-step" data-step="1">
      <h2 class="wizard-step-title">성별</h2>
      <p class="wizard-step-subtitle">어떤 성별의 점술가를 만들까요?</p>

      <div class="wizard-gender-options">
        <button class="wizard-gender-card ${state.gender === '여성' ? 'selected' : ''}"
                data-action="selectGender" data-value="여성">
          <span class="wizard-gender-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="8" r="5"/>
              <path d="M12 13v8"/>
              <path d="M9 18h6"/>
            </svg>
          </span>
          <span class="wizard-gender-label">여성</span>
        </button>
        <button class="wizard-gender-card ${state.gender === '남성' ? 'selected' : ''}"
                data-action="selectGender" data-value="남성">
          <span class="wizard-gender-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="10" cy="14" r="5"/>
              <path d="M19 5l-5.4 5.4"/>
              <path d="M15 5h4v4"/>
            </svg>
          </span>
          <span class="wizard-gender-label">남성</span>
        </button>
      </div>
    </div>
  `;
}

// ============================================
// Step 2: 캐릭터 설명 (자유 입력)
// ============================================

function renderStep2(state) {
  return `
    <div class="wizard-step" data-step="2">
      <h2 class="wizard-step-title">점술가 설명</h2>
      <p class="wizard-step-subtitle">만들고 싶은 점술가를 알려주세요!<br>말투, 성격, 점술 스타일 뭐든지 좋아요</p>

      <textarea
        id="wizardDescription"
        class="wizard-textarea"
        placeholder="예: 30년 경력의 따뜻한 사주 전문가&#10;직설적이지만 희망적인 조언을 해주는 타입"
        data-action="updateDescription"
      >${escapeHtml(state.description || '')}</textarea>
    </div>
  `;
}

// ============================================
// Step 3: 점술 스타일 태그 선택
// ============================================

function renderStep3(state) {
  const selectedTags = state.styleTags || [];
  const customTags = state.customStyleTags || [];

  return `
    <div class="wizard-step" data-step="3">
      <h2 class="wizard-step-title">점술 스타일</h2>
      <p class="wizard-step-subtitle">점술가의 말투와 스타일을 알려주세요<br>선택하지 않으면 AI가 만들어요!</p>

      <div class="wizard-custom-input">
        <input type="text" id="wizardCustomTagInput" class="wizard-input"
               placeholder="직접 입력" maxlength="20" data-tag-type="style">
        <button class="wizard-add-btn" data-action="addCustomTag" data-tag-type="style">추가</button>
      </div>

      ${customTags.length > 0 ? `
        <div class="wizard-custom-tags">
          <div class="wizard-custom-tags-list">
            ${customTags.map(tag => `
              <span class="wizard-custom-tag-item">
                ${escapeHtml(tag)}
                <button class="wizard-custom-tag-remove" data-action="removeCustomTag" data-tag="${escapeHtml(tag)}" data-tag-type="style">×</button>
              </span>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <div class="wizard-tag-grid">
        ${FORTUNE_STYLE_TAGS.map(tag => `
          <button class="wizard-tag-chip ${selectedTags.includes(tag) ? 'selected' : ''}"
                  data-action="toggleTag" data-tag="${escapeHtml(tag)}" data-tag-type="style">
            ${escapeHtml(tag)}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}

// ============================================
// Step 4: 점술 유형 태그 선택
// ============================================

function renderStep4(state) {
  const selectedTags = state.fortuneTypeTags || [];
  const customTags = state.customFortuneTypeTags || [];

  return `
    <div class="wizard-step" data-step="4">
      <h2 class="wizard-step-title">점술 유형</h2>
      <p class="wizard-step-subtitle">어떤 점술을 보는 점술가인가요?<br>선택하지 않으면 AI가 만들어요!</p>

      <div class="wizard-custom-input">
        <input type="text" id="wizardCustomFortuneTypeInput" class="wizard-input"
               placeholder="직접 입력" maxlength="20" data-tag-type="fortuneType">
        <button class="wizard-add-btn" data-action="addCustomTag" data-tag-type="fortuneType">추가</button>
      </div>

      ${customTags.length > 0 ? `
        <div class="wizard-custom-tags">
          <div class="wizard-custom-tags-list">
            ${customTags.map(tag => `
              <span class="wizard-custom-tag-item">
                ${escapeHtml(tag)}
                <button class="wizard-custom-tag-remove" data-action="removeCustomTag" data-tag="${escapeHtml(tag)}" data-tag-type="fortuneType">×</button>
              </span>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <div class="wizard-tag-grid">
        ${FORTUNE_TYPE_TAGS.map(tag => `
          <button class="wizard-tag-chip ${selectedTags.includes(tag) ? 'selected' : ''}"
                  data-action="toggleTag" data-tag="${escapeHtml(tag)}" data-tag-type="fortuneType">
            ${escapeHtml(tag)}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}

// ============================================
// Step 5: 전문 분야 태그 선택
// ============================================

function renderStep5(state) {
  const selectedTags = state.specialtyTags || [];
  const customTags = state.customSpecialtyTags || [];

  return `
    <div class="wizard-step" data-step="5">
      <h2 class="wizard-step-title">전문 분야</h2>
      <p class="wizard-step-subtitle">어떤 운세를 주로 봐주나요?<br>선택하지 않으면 AI가 만들어요!</p>

      <div class="wizard-custom-input">
        <input type="text" id="wizardCustomSpecialtyInput" class="wizard-input"
               placeholder="직접 입력" maxlength="20" data-tag-type="specialty">
        <button class="wizard-add-btn" data-action="addCustomTag" data-tag-type="specialty">추가</button>
      </div>

      ${customTags.length > 0 ? `
        <div class="wizard-custom-tags">
          <div class="wizard-custom-tags-list">
            ${customTags.map(tag => `
              <span class="wizard-custom-tag-item">
                ${escapeHtml(tag)}
                <button class="wizard-custom-tag-remove" data-action="removeCustomTag" data-tag="${escapeHtml(tag)}" data-tag-type="specialty">×</button>
              </span>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <div class="wizard-tag-grid">
        ${SPECIALTY_TAGS.map(tag => `
          <button class="wizard-tag-chip ${selectedTags.includes(tag) ? 'selected' : ''}"
                  data-action="toggleTag" data-tag="${escapeHtml(tag)}" data-tag-type="specialty">
            ${escapeHtml(tag)}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}

// ============================================
// Step 6: 점술가 배경 (선택적)
// ============================================

function renderStep6(state) {
  return `
    <div class="wizard-step" data-step="6">
      <h2 class="wizard-step-title">점술가 배경</h2>
      <p class="wizard-step-subtitle">점술가의 배경이나 점술소 분위기를 설명해주세요<br>입력하지 않으면 AI가 만들어줘요</p>

      <textarea
        id="wizardIntroScene"
        class="wizard-textarea"
        placeholder="예: 서울 종로의 한 골목에 있는 작은 점술소&#10;향 냄새가 은은하게 퍼지는 아늑한 공간"
        data-action="updateIntroScene"
      >${escapeHtml(state.introScene || '')}</textarea>
    </div>
  `;
}

// ============================================
// Step 7: 이미지 스타일 선택
// ============================================

function renderStep7(state) {
  return `
    <div class="wizard-step" data-step="7">
      <h2 class="wizard-step-title">이미지 스타일</h2>
      <p class="wizard-step-subtitle">점술가 이미지를 어떤 스타일로 만들까요?</p>

      <div id="wizardModelCarousel" class="wizard-model-carousel">
        <div class="wizard-model-loading">불러오는 중...</div>
      </div>
    </div>
  `;
}

// ============================================
// 이미지 모델 캐러셀 렌더링 (외부에서 호출)
// ============================================

export function renderImageModelCarousel(models, selectedModelId) {
  const carousel = document.getElementById('wizardModelCarousel');
  if (!carousel) return;

  if (!models || models.length === 0) {
    carousel.innerHTML = '<div class="wizard-model-empty">이미지 스타일을 불러올 수 없어요</div>';
    return;
  }

  // 카본 아이콘 SVG
  const carbonIcon = `<svg class="wizard-carbon-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>`;

  // "이미지 안함" 옵션 추가 (첫 번째로)
  const noImageCard = `
    <div class="wizard-model-card no-image ${selectedModelId === 'none' ? 'selected' : ''}"
         data-action="selectImageModel" data-model-id="none">
      <div class="wizard-model-image">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M3 3l18 18M10.5 10.5a2.5 2.5 0 1 0 3.5 3.5"/>
          <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>
        </svg>
      </div>
      <div class="wizard-model-info">
        <span class="wizard-model-name">이미지 안함</span>
        <span class="wizard-model-cost">${carbonIcon} 0</span>
      </div>
    </div>
  `;

  const modelCards = models.map(model => `
    <div class="wizard-model-card ${selectedModelId === model.id ? 'selected' : ''}"
         data-action="selectImageModel" data-model-id="${escapeHtml(model.id)}">
      <img class="wizard-model-image" src="${escapeHtml(model.image || '')}" alt="${escapeHtml(model.name)}" loading="lazy">
      <div class="wizard-model-info">
        <span class="wizard-model-name">${escapeHtml(model.name)}</span>
        <span class="wizard-model-cost">${carbonIcon} +${model.carbonCost || 0}</span>
      </div>
    </div>
  `).join('');

  carousel.innerHTML = noImageCard + modelCards;
}

// ============================================
// 이미지 비용 업데이트 (더 이상 사용 안함)
// ============================================

export function updateImageCostDisplay(cost) {
  // 토글이 없어져서 더 이상 필요 없음
  // 생성 버튼에 직접 표시
}
